import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { Link } from "react-router-dom";

const INTRO = [
  { role: "bot", text: "Hi, I'm Vera — your Venvay style concierge. Ask me anything!" },
  { role: "bot", text: "Try: coupons · shipping · sizes · buy-2-get-1 · payment · contact" },
];

function answer(q) {
  const s = q.toLowerCase();
  if (/coupon|discount|promo|offer/.test(s))
    return { text: "Active coupons: WELCOME10 (10% off ₹999+), FLAT500 (₹500 off ₹2999+), VENVAY20 (20% off ₹1999+). Apply at cart.", link: { label: "Browse products", to: "/men" } };
  if (/ship|delivery|dispatch/.test(s))
    return { text: "Free shipping on orders over ₹1499. Standard delivery 3–6 business days across India." };
  if (/size|fit/.test(s))
    return { text: "Men: S–XL for shirts, 30–36 for pants/jeans. Women: S–L / 26–30. Not sure? Message us on WhatsApp for a fit check." };
  if (/buy 2|2\+1|buy2|combo|three|3/.test(s))
    return { text: "Buy 2 Get 1 Free — add any 3 apparel items, cheapest is on us. Auto-applied at checkout." };
  if (/pay|payment|cod|razor/.test(s))
    return { text: "We accept Cash on Delivery (COD) and Razorpay (UPI, Cards, Wallets, Netbanking) at checkout." };
  if (/return|refund|exchange/.test(s))
    return { text: "Easy 7-day returns on unworn items with tags. Refunds hit your account in 5–7 days." };
  if (/gift/.test(s))
    return { text: "Our Gifts section has curated hampers, signature boxes and festive picks.", link: { label: "Shop gifts", to: "/gifts" } };
  if (/contact|help|human|whatsapp|call/.test(s))
    return { text: "Reach us on WhatsApp +91 90962 13765 or email venvayoffical@gmail.com — we usually reply within 2 hours.", link: { label: "WhatsApp Us", href: "https://wa.me/919096213765" } };
  if (/order|track/.test(s))
    return { text: "Sign in and open My Orders to track. You'll also get email updates on every status change.", link: { label: "My Orders", to: "/orders" } };
  if (/hi|hello|hey/.test(s))
    return { text: "Hey! Looking for something specific? Try our new arrivals or featured picks.", link: { label: "Featured", to: "/" } };
  return { text: "Great question! I'll flag this to our team — meanwhile, WhatsApp us at +91 90962 13765 for a quick reply.", link: { label: "WhatsApp", href: "https://wa.me/919096213765" } };
}

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(INTRO);
  const [input, setInput] = useState("");
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = (text) => {
    const t = (text ?? input).trim();
    if (!t) return;
    setMsgs((m) => [...m, { role: "user", text: t }]);
    setInput("");
    setTimeout(() => {
      const a = answer(t);
      setMsgs((m) => [...m, { role: "bot", ...a }]);
    }, 400);
  };

  return (
    <>
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-[#8B2E2E] text-white shadow-lg hover:scale-105 active:scale-95 transition-transform flex items-center justify-center"
        aria-label="Open chat"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div data-testid="chatbot-panel" className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm bg-[#F5F1EB] border border-black/10 rounded-lg shadow-2xl overflow-hidden animate-fadeUp">
          <div className="bg-[#0A0A0A] text-[#F5F1EB] px-4 py-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-[#8B2E2E] flex items-center justify-center font-display font-bold">V</div>
            <div>
              <p className="font-display text-lg leading-none">Vera</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/60 mt-0.5">Style Concierge · Online</p>
            </div>
          </div>
          <div className="p-4 h-80 overflow-y-auto space-y-3 bg-[#F5F1EB]">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${m.role === "user" ? "bg-[#0A0A0A] text-white" : "bg-white border border-black/5 text-[#0A0A0A]"}`}>
                  <p>{m.text}</p>
                  {m.link && m.link.to && (
                    <Link to={m.link.to} onClick={() => setOpen(false)} className="inline-block mt-2 text-xs font-semibold text-[#8B2E2E] underline">
                      {m.link.label} →
                    </Link>
                  )}
                  {m.link && m.link.href && (
                    <a href={m.link.href} target="_blank" rel="noreferrer" className="inline-block mt-2 text-xs font-semibold text-[#8B2E2E] underline">
                      {m.link.label} →
                    </a>
                  )}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-black/10 bg-white flex gap-2">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type a message…"
              className="flex-1 px-3 py-2 rounded-md border border-black/10 text-sm outline-none focus:ring-2 focus:ring-[#8B2E2E]/30"
            />
            <button data-testid="chatbot-send" onClick={() => send()} className="h-9 w-9 rounded-md bg-[#8B2E2E] text-white flex items-center justify-center hover:opacity-90">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
